import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { OrderService, OrderListItem } from '../services/order.service';

@Component({
  selector: 'app-seller-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule],
  templateUrl: './seller-orders.component.html',
  styleUrl: './seller-orders.component.css'
})
export class SellerOrdersComponent implements OnInit {
  readonly maxRangeDays = 7;

  startDate = '';
  endDate = '';
  today = '';

  orders: OrderListItem[] = [];
  total = 0;
  loading = false;
  errorMessage = '';

  skip = 0;
  limit = 50;

  constructor(
    private orderService: OrderService,
    private toastr: ToastrService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    const today = new Date();
    const start = new Date();
    start.setDate(today.getDate() - (this.maxRangeDays - 1));

    this.today = this.toInputDate(today);
    this.endDate = this.toInputDate(today);
    this.startDate = this.toInputDate(start);

    this.search();
  }

  search(): void {
    if (!this.validateRange()) {
      this.toastr.error(this.errorMessage);
      return;
    }
    this.skip = 0;
    this.load();
  }

  nextPage(): void {
    if (this.skip + this.limit >= this.total) {
      return;
    }
    this.skip += this.limit;
    this.load();
  }

  previousPage(): void {
    if (this.skip === 0) {
      return;
    }
    this.skip = Math.max(0, this.skip - this.limit);
    this.load();
  }

  get currentPage(): number {
    return Math.floor(this.skip / this.limit) + 1;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.limit));
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  }

  private load(): void {
    this.loading = true;
    this.orderService.getOrdersByDateRange(this.startDate, this.endDate, this.skip, this.limit)
      .subscribe({
        next: (response) => {
          this.orders = response.items || [];
          this.total = response.total || 0;
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar os pedidos:', error);
          this.orders = [];
          this.total = 0;
          this.loading = false;
          this.toastr.error(this.transloco.translate('sellerOrders.errors.loadError'));
        }
      });
  }

  private validateRange(): boolean {
    this.errorMessage = '';

    if (!this.startDate || !this.endDate) {
      this.errorMessage = this.transloco.translate('sellerOrders.errors.required');
      return false;
    }

    const start = new Date(this.startDate);
    const end = new Date(this.endDate);

    if (end < start) {
      this.errorMessage = this.transloco.translate('sellerOrders.errors.endBeforeStart');
      return false;
    }

    const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays > this.maxRangeDays) {
      this.errorMessage = this.transloco.translate('sellerOrders.errors.rangeTooLarge', { max: this.maxRangeDays });
      return false;
    }

    return true;
  }

  private toInputDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
